import { ReturnsFlagKey } from "@redotech/feature-flags-keys-returns/keys";
import {
  isReturnsSettingsTab,
  RETURNS_SETTINGS_TAB_BY_LEGACY_PATH,
  RETURNS_SETTINGS_TAB_QUERY_PARAM,
  type ReturnsSettingsTab,
} from "../flat-team-settings/returns/returns-settings-tab";
import { RETURNS_CONSOLIDATED_SETTINGS_PATH } from "../flat-team-settings/returns/settings-paths";
import { getSettingCatalogGroup } from "./index";
import { settingsWrapperManifest } from "./settings-wrapper-manifest.generated";
import { hasShippingNewSettings } from "./shipping-new-settings";
import type {
  SettingAvailabilityContext,
  SettingGroupFullPageTarget,
} from "./types";

/** The team is on the new returns settings page. */
export function hasReturnsNewSettings(
  context?: SettingAvailabilityContext,
): boolean {
  const evaluatorData = context?.evaluatorData;
  return (
    evaluatorData?.featureFlagsByKey?.[
      ReturnsFlagKey.CONSOLIDATED_RETURNS_SETTINGS
    ] === true
  );
}

const TAB_DIRECTORY = /^returns\/([^/]+)\//;

let tabByGroupId: ReadonlyMap<string, ReturnsSettingsTab> | undefined;
let tabBySettingId: ReadonlyMap<string, ReturnsSettingsTab> | undefined;

function tabOfFile(file: string): ReturnsSettingsTab | undefined {
  const directory = TAB_DIRECTORY.exec(file)?.[1] ?? null;
  return isReturnsSettingsTab(directory) ? directory : undefined;
}

/** A group's wrappers can span tabs; the tab holding most of them wins. */
function buildTabByGroupId(): ReadonlyMap<string, ReturnsSettingsTab> {
  const countsByGroupId = new Map<string, Map<ReturnsSettingsTab, number>>();
  const count = (groupId: string, file: string) => {
    const tab = tabOfFile(file);
    if (!tab) {
      return;
    }
    const counts =
      countsByGroupId.get(groupId) ?? new Map<ReturnsSettingsTab, number>();
    counts.set(tab, (counts.get(tab) ?? 0) + 1);
    countsByGroupId.set(groupId, counts);
  };
  for (const section of settingsWrapperManifest.sections) {
    count(section.id, section.file);
  }
  for (const setting of settingsWrapperManifest.settings) {
    if (setting.sectionId) {
      count(setting.sectionId, setting.file);
    }
  }

  const result = new Map<string, ReturnsSettingsTab>();
  for (const [groupId, counts] of countsByGroupId) {
    const [top] = [...counts].sort(([, a], [, b]) => b - a);
    if (top) {
      result.set(groupId, top[0]);
    }
  }
  return result;
}

function buildTabBySettingId(): ReadonlyMap<string, ReturnsSettingsTab> {
  const result = new Map<string, ReturnsSettingsTab>();
  for (const setting of settingsWrapperManifest.settings) {
    const tab = tabOfFile(setting.file);
    if (tab && !result.has(setting.id)) {
      result.set(setting.id, tab);
    }
  }
  return result;
}

/** The setting's own tab when given, since a split group's majority tab may not hold it. */
export function getReturnsSettingsTabForGroup(
  groupId: string,
  settingId?: string,
): ReturnsSettingsTab | undefined {
  tabByGroupId ??= buildTabByGroupId();
  tabBySettingId ??= buildTabBySettingId();
  return (
    (settingId && tabBySettingId.get(settingId)) || tabByGroupId.get(groupId)
  );
}

export function getReturnsSettingsTabPath(tab: ReturnsSettingsTab): string {
  return `${RETURNS_CONSOLIDATED_SETTINGS_PATH}?${RETURNS_SETTINGS_TAB_QUERY_PARAM}=${tab}`;
}

/** A flow editor or other page under an old one; the new page links out to it too. */
function isNestedPage(path: string): boolean {
  return path.includes("/") && !(path in RETURNS_SETTINGS_TAB_BY_LEGACY_PATH);
}

/** The group's tab on the new page for a merchant using it, else its old page. */
export function resolveReturnsSettingsFullPageTarget({
  groupId,
  fullPageTarget,
  returnsNewSettings,
}: {
  groupId: string;
  fullPageTarget: SettingGroupFullPageTarget | undefined;
  returnsNewSettings: boolean;
}): SettingGroupFullPageTarget | undefined {
  const tab = getReturnsSettingsTabForGroup(groupId);
  if (
    !returnsNewSettings ||
    !tab ||
    fullPageTarget?.type !== "settings" ||
    isNestedPage(fullPageTarget.path)
  ) {
    return fullPageTarget;
  }
  return { type: "settings", path: getReturnsSettingsTabPath(tab) };
}

export interface ReturnsSettingsLocations {
  newPage: { path: string; tab: ReturnsSettingsTab };
}

/** Where a merchant on the new page finds the group. */
export function getReturnsSettingsLocations({
  groupId,
  context,
}: {
  groupId: string;
  context: SettingAvailabilityContext | undefined;
}): ReturnsSettingsLocations | undefined {
  const tab = getReturnsSettingsTabForGroup(groupId);
  const fullPageTarget = getSettingCatalogGroup(
    groupId,
    hasShippingNewSettings(context),
  )?.fullPageTarget;
  if (
    !hasReturnsNewSettings(context) ||
    !tab ||
    fullPageTarget?.type !== "settings" ||
    isNestedPage(fullPageTarget.path)
  ) {
    return undefined;
  }
  return { newPage: { path: getReturnsSettingsTabPath(tab), tab } };
}

export const RETURNS_SETTINGS_LOCATIONS_GUIDANCE =
  'A result with "returnsSettingsLocations" belongs to a team on the new Returns settings page. When you say where to find it, name the tab on the Returns settings page (newPage.tab), not the old settings page the result\'s path points at.';
